import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState } from "react";
import { subirDatos, eliminarDato } from "./syncSupabase";

// ─── CONSTANTES ───────────────────────────────────────────────
const STORAGE_FSC = 'alqos_fsc_arqueo';
const STORAGE_NC = 'alqos_ncs';

const LOG = {
  info: (msg, data) => console.log(`[FSC][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[FSC][ERROR] ${msg}`, err || ''),
  warn: (msg, data) => console.warn(`[FSC][WARN] ${msg}`, data || ''),
};

const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { LOG.error(`get ${key}`, e); return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { LOG.error(`set ${key}`, e); return false; } },
};

// ─── CONSTANTES DE DOMINIO ────────────────────────────────────
const tiposMovimiento = {
  ENTRADA: { label: "📥 Entrada", color: "#22c55e" },
  SALIDA: { label: "📤 Salida", color: "#3b82f6" },
  SCRAP: { label: "🗑️ Scrap", color: "#e63946" },
};

const origenesMovimiento = ["Compra", "Producción", "Scrap proceso", "Ajuste inventario", "Devolución", "Otro"];
const certificadosFSC = ["FSC 100%", "FSC Mix Credit", "FSC Mix X%", "FSC Recycled", "Controlled Wood"];

// ─── FUNCIONES DE LÓGICA ──────────────────────────────────────
function generarIdMov(lista) {
  const anio = new Date().getFullYear();
  const num = String(lista.length + 1).padStart(4, "0");
  return `FSC-${anio}-${num}`;
}

function fechaHoy() { return new Date().toISOString().slice(0, 10); }

function calcularStock(movimientos) {
  return movimientos.reduce((acc, m) => {
    return acc + (Number(m.kgEntrada) || 0) - (Number(m.kgSalida) || 0) - (Number(m.kgScrap) || 0);
  }, 0);
}

function calcularStockAcumulado(movimientos) {
  let stock = 0;
  return movimientos.map(m => {
    stock += (Number(m.kgEntrada) || 0) - (Number(m.kgSalida) || 0) - (Number(m.kgScrap) || 0);
    return { ...m, stockAcumulado: Math.round(stock * 100) / 100 };
  });
}

function exportarCSV(datos, nombre) {
  try {
    if (!datos.length) { alert("No hay datos para exportar."); return; }
    const keys = Object.keys(datos[0]).filter(k => !k.startsWith('_'));
    const csv = [keys.join(","), ...datos.map(d => keys.map(k => `"${String(d[k] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${nombre}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    LOG.info("CSV FSC exportado", { registros: datos.length });
  } catch (err) { LOG.error("Error exportar CSV FSC", err); }
}

function exportarPDF(datos, resumen) {
  try {
    if (!datos.length) { alert("No hay datos."); return; }
    const filas = datos.map(d => `
      <tr>
        <td>${d.idMovimiento}</td><td>${d.fecha}</td>
        <td style="color:${tiposMovimiento[d.tipoMovimiento]?.color}">${d.tipoMovimiento}</td>
        <td>${d.producto}</td><td>${d.descripcion || "—"}</td>
        <td style="color:#22c55e">${d.kgEntrada || "—"}</td>
        <td style="color:#3b82f6">${d.kgSalida || "—"}</td>
        <td style="color:#e63946">${d.kgScrap || "—"}</td>
        <td style="font-weight:700">${d.stockAcumulado ?? "—"}</td>
        <td>${d.certificadoFSC || "—"}</td>
        <td>${d.idNC || "—"}</td>
      </tr>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Arqueo FSC</title>
    <style>body{font-family:Arial,sans-serif;font-size:10px;padding:20px}
    h1{color:#09184D;font-size:16px}h2{color:#22c55e;font-size:12px}
    .resumen{display:flex;gap:20px;margin:12px 0;flex-wrap:wrap}
    .kpi{background:#f0f8ff;border:1px solid #bde0fe;padding:8px 12px;border-radius:6px;min-width:100px}
    .kpi p{margin:0;font-size:10px;color:#808085}.kpi strong{font-size:14px;color:#09184D}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    th{background:#09184D;color:white;padding:5px 6px;font-size:9px}
    td{padding:4px 6px;border-bottom:.5px solid #ddd;font-size:9px}
    tr:nth-child(even){background:#f7f7f7}</style></head>
    <body><h1>ALQOS™ — Arqueo FSC Chain of Custody</h1>
    <h2>Balance de masa certificado</h2>
    <p style="font-size:10px;color:#808085">Generado: ${new Date().toLocaleString('es-AR')}</p>
    <div class="resumen">
      <div class="kpi"><p>Total Entradas</p><strong>${resumen.totalEntradas} kg</strong></div>
      <div class="kpi"><p>Total Salidas</p><strong>${resumen.totalSalidas} kg</strong></div>
      <div class="kpi"><p>Total Scrap</p><strong>${resumen.totalScrap} kg</strong></div>
      <div class="kpi"><p>Stock Actual</p><strong style="color:${resumen.stockActual < 0 ? '#e63946' : '#22c55e'}">${resumen.stockActual} kg</strong></div>
    </div>
    <table><thead><tr><th>ID</th><th>Fecha</th><th>Tipo</th><th>Producto</th><th>Descripción</th>
    <th>Entrada(kg)</th><th>Salida(kg)</th><th>Scrap(kg)</th><th>Stock Acum.</th><th>Certif.</th><th>NC</th></tr></thead>
    <tbody>${filas}</tbody></table>
    <p style="font-size:9px;color:#808085;margin-top:20px">Documento FSC Chain of Custody generado por ALQOS™ — Confidencial — Solo para uso interno y auditoría</p>
    </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 500);
    LOG.info("PDF FSC generado", { registros: datos.length });
  } catch (err) { LOG.error("Error exportar PDF FSC", err); }
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────
export default function FSCArqueo({ onVolver, oscuro, usuario }) {
  const [movimientos, setMovimientos] = useState(() => {
    const raw = storage.get(STORAGE_FSC);
    return calcularStockAcumulado(raw);
  });
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ tipoMovimiento: "ENTRADA", origen: "Compra", certificadoFSC: "FSC 100%", fecha: fechaHoy() });
  const [detalle, setDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [errores, setErrores] = useState({});
  const [filtroTipo, setFiltroTipo] = useState("todos");

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";

  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  const stockActual = Math.round(calcularStock(movimientos) * 100) / 100;
  const totalEntradas = Math.round(movimientos.reduce((a, m) => a + (Number(m.kgEntrada) || 0), 0) * 100) / 100;
  const totalSalidas = Math.round(movimientos.reduce((a, m) => a + (Number(m.kgSalida) || 0), 0) * 100) / 100;
  const totalScrap = Math.round(movimientos.reduce((a, m) => a + (Number(m.kgScrap) || 0), 0) * 100) / 100;

  // ─── VALIDACIÓN ───────────────────────────────────────────
  const validar = () => {
    const e = {};
    if (!form.fecha) e.fecha = "Requerido";
    if (!form.producto?.trim()) e.producto = "Requerido";
    if (!form.certificadoFSC) e.certificadoFSC = "Requerido";

    const entrada = Number(form.kgEntrada) || 0;
    const salida = Number(form.kgSalida) || 0;
    const scrap = Number(form.kgScrap) || 0;

    if (entrada < 0 || salida < 0 || scrap < 0) e.kg = "No se permiten valores negativos";
    if (entrada > 0 && salida > 0) e.kg = "No se puede registrar entrada y salida en el mismo movimiento";
    if (entrada > 0 && scrap > 0 && salida > 0) e.kg = "Solo un tipo de movimiento por registro";
    if (entrada === 0 && salida === 0 && scrap === 0) e.kg = "Ingresá al menos un valor en kg";

    // Verificar stock negativo
    const stockProyectado = stockActual - salida - scrap;
    if (stockProyectado < 0 && form.tipoMovimiento !== "ENTRADA") {
      e.stockNegativo = `⚠️ Esta operación dejaría el stock en ${stockProyectado.toFixed(2)} kg — stock negativo no permitido en FSC`;
    }

    setErrores(e);
    LOG.info("Validación FSC", { errores: Object.keys(e).length });
    return Object.keys(e).length === 0;
  };

  // ─── GUARDADO ─────────────────────────────────────────────
  const guardar = () => {
    if (!validar()) return;
    try {
      const actuales = storage.get(STORAGE_FSC);
      const nuevo = {
        ...form,
        id: Date.now(),
        idMovimiento: generarIdMov(actuales),
        kgEntrada: Number(form.kgEntrada) || 0,
        kgSalida: Number(form.kgSalida) || 0,
        kgScrap: Number(form.kgScrap) || 0,
        creadoPor: usuario?.nombre || "Sistema",
        fechaRegistro: new Date().toISOString(),
        _source: "localStorage",
        _schema: "fsc_v1",
      };
      const nuevos = [...actuales, nuevo];
      storage.set(STORAGE_FSC, nuevos);
      subirDatos(STORAGE_FSC);
      const conStock = calcularStockAcumulado(nuevos);
      setMovimientos(conStock);

      // Si es SCRAP → ofrecer generar NC
      if (nuevo.tipoMovimiento === "SCRAP" && nuevo.kgScrap > 0) {
        const generar = window.confirm(`¿Deseás generar una No Conformidad por el scrap de ${nuevo.kgScrap} kg de ${nuevo.producto}?`);
        if (generar) {
          const ncs = storage.get(STORAGE_NC);
          const anio = new Date().getFullYear();
          const nci = `NCI-${anio}-${String(ncs.length + 1).padStart(3, "0")}`;
          const ncScrap = {
            nci, id: Date.now() + 1,
            origen: "FSC Arqueo — Scrap",
            tipoNC: "Incumplimiento FSC",
            proceso: nuevo.origen,
            producto: nuevo.producto,
            descripcion: `Scrap FSC registrado: ${nuevo.kgScrap} kg de ${nuevo.producto}. Certificado: ${nuevo.certificadoFSC}. Movimiento: ${nuevo.idMovimiento}`,
            escalamiento: "medio",
            severidad: "mayor",
            estado: "abierta",
            idMovimientoFSC: nuevo.idMovimiento,
            fechaHora: new Date().toISOString().slice(0, 16),
            creadoPor: usuario?.nombre || "Sistema",
            rolCreador: usuario?.rol || "",
            generadaAutomaticamente: true,
            _generadaDesde: "fsc_arqueo",
            _source: "localStorage",
            _schema: "nc_v1",
          };
          storage.set(STORAGE_NC, [...ncs, ncScrap]);
          subirDatos(STORAGE_NC);
          LOG.info("NC generada desde scrap FSC", { nci, movimiento: nuevo.idMovimiento });
          alert(`NC generada automáticamente: ${nci}`);
        }
      }

      LOG.info("Movimiento FSC guardado", { id: nuevo.idMovimiento, tipo: nuevo.tipoMovimiento, stockActual: stockActual });
      setForm({ tipoMovimiento: "ENTRADA", origen: "Compra", certificadoFSC: "FSC 100%", fecha: fechaHoy() });
      setErrores({});
      setVista("listado");
    } catch (err) {
      LOG.error("Error al guardar movimiento FSC", err);
      alert("Error inesperado.");
    }
  };

  // ─── ELIMINAR ─────────────────────────────────────────────
  const eliminar = async (id) => {
    try {
      const nuevos = storage.get(STORAGE_FSC).filter(m => m.id !== id);
      storage.set(STORAGE_FSC, nuevos);
      await eliminarDato(STORAGE_FSC, id);
      const conStock = calcularStockAcumulado(nuevos);
      setMovimientos(conStock);
      LOG.info("Movimiento FSC eliminado", { id });
      setConfirmEliminar(null); setDetalle(null); setVista("listado");
    } catch (err) { LOG.error("Error eliminar movimiento", err); }
  };

  const movsFiltrados = filtroTipo === "todos" ? movimientos : movimientos.filter(m => m.tipoMovimiento === filtroTipo);

  // ─── UI ───────────────────────────────────────────────────
  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🌿</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar este movimiento?</p>
        <p style={{ fontSize: "13px", color: subtexto, marginBottom: "1.5rem" }}>El balance de masa se recalculará automáticamente.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  if (vista === "detalle" && detalle) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo={detalle.idMovimiento}
        onBack={() => { setDetalle(null); setVista("listado"); }}
        extra={esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)}
          style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>}
      />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${tiposMovimiento[detalle.tipoMovimiento]?.color || "#7B5BF2"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", margin: 0 }}>Datos del movimiento FSC</h3>
            <span style={{ fontSize: "12px", fontWeight: "700", padding: "3px 10px", borderRadius: "10px", background: `${tiposMovimiento[detalle.tipoMovimiento]?.color}22`, color: tiposMovimiento[detalle.tipoMovimiento]?.color, border: `1px solid ${tiposMovimiento[detalle.tipoMovimiento]?.color}` }}>
              {tiposMovimiento[detalle.tipoMovimiento]?.label}
            </span>
          </div>
          {[["ID Movimiento", detalle.idMovimiento], ["Fecha", detalle.fecha], ["Producto", detalle.producto], ["Descripción", detalle.descripcion], ["Origen", detalle.origen], ["Certificado FSC", detalle.certificadoFSC], ["OT vinculada", detalle.ot], ["Proveedor", detalle.proveedor], ["Entrada (kg)", detalle.kgEntrada ? `${detalle.kgEntrada} kg` : null], ["Salida (kg)", detalle.kgSalida ? `${detalle.kgSalida} kg` : null], ["Scrap (kg)", detalle.kgScrap ? `${detalle.kgScrap} kg` : null], ["Stock acumulado", `${detalle.stockAcumulado ?? "—"} kg`], ["ID NC vinculada", detalle.idNC], ["ID Plan Control", detalle.idPlanControl], ["ID FMEA", detalle.idFMEA], ["Creado por", `${detalle.creadoPor}`]].map(([k, v]) => v && (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
              <span style={{ color: subtexto }}>{k}</span>
              <span style={{ color: k === "Stock acumulado" ? (detalle.stockAcumulado < 0 ? "#e63946" : "#22c55e") : texto, fontWeight: k === "Stock acumulado" ? "700" : "500" }}>{v}</span>
            </div>
          ))}
        </div>
        {detalle.observaciones && (
          <div style={card}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#09184D", marginBottom: "8px" }}>Observaciones</h3>
            <p style={{ fontSize: "13px", color: texto, margin: 0 }}>{detalle.observaciones}</p>
          </div>
        )}
      </div>
    </div>
  );

  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nuevo Movimiento FSC" onBack={() => { setVista("listado"); setErrores({}); }} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>

        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "4px" }}>Trazabilidad automática FSC</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>Creado por: <strong style={{ color: texto }}>{usuario?.nombre}</strong> — {usuario?.rol}</p>
        </div>

        {/* TIPO DE MOVIMIENTO */}
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#09184D" }}>Tipo de movimiento FSC</h3>
          <div style={{ display: "flex", gap: "8px" }}>
            {Object.entries(tiposMovimiento).map(([val, { label, color }]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, tipoMovimiento: val }))}
                style={{ flex: 1, padding: "10px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "700", border: form.tipoMovimiento === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.tipoMovimiento === val ? `${color}22` : cardBg, color: form.tipoMovimiento === val ? color : texto }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* DATOS DEL MOVIMIENTO */}
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#22c55e" }}>Datos del material FSC</h3>
          <span style={lbl}>Fecha *</span>
          <input type="date" defaultValue={fechaHoy()} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={{ ...inp, borderColor: errores.fecha ? "#e63946" : borde }} />
          <span style={lbl}>Producto * {errores.producto && <span style={{ color: "#e63946" }}>— {errores.producto}</span>}</span>
          <input onChange={e => setForm(f => ({ ...f, producto: e.target.value }))} style={{ ...inp, borderColor: errores.producto ? "#e63946" : borde }} placeholder="Ej: Cartulina Gráfica 350g FSC" />
          <span style={lbl}>Descripción</span>
          <input onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={inp} placeholder="Detalle adicional del movimiento" />
          <span style={lbl}>Certificado FSC * {errores.certificadoFSC && <span style={{ color: "#e63946" }}>— {errores.certificadoFSC}</span>}</span>
          <select onChange={e => setForm(f => ({ ...f, certificadoFSC: e.target.value }))} style={{ ...inp, borderColor: errores.certificadoFSC ? "#e63946" : borde }} defaultValue="FSC 100%">
            {certificadosFSC.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span style={lbl}>Origen del movimiento</span>
          <select onChange={e => setForm(f => ({ ...f, origen: e.target.value }))} style={inp} defaultValue="Compra">
            {origenesMovimiento.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <span style={lbl}>Proveedor FSC (si es entrada)</span>
          <input onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))} style={inp} placeholder="Nombre y código de certificación del proveedor" />
          <span style={lbl}>OT vinculada (si es salida a producción)</span>
          <input onChange={e => setForm(f => ({ ...f, ot: e.target.value }))} style={inp} placeholder="Ej: OT-2026-0042" />
        </div>

        {/* CANTIDADES */}
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#3b82f6" }}>Balance de masa (kg)</h3>
          <p style={{ fontSize: "12px", color: subtexto, marginBottom: "12px" }}>Stock actual: <strong style={{ color: stockActual < 0 ? "#e63946" : "#22c55e" }}>{stockActual} kg</strong> — Solo completá el campo correspondiente al tipo de movimiento seleccionado.</p>
          {errores.kg && <p style={{ color: "#e63946", fontSize: "12px", margin: "0 0 10px", fontWeight: "600" }}>⚠️ {errores.kg}</p>}
          {errores.stockNegativo && <p style={{ color: "#e63946", fontSize: "12px", margin: "0 0 10px", fontWeight: "600" }}>{errores.stockNegativo}</p>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
            {[{ key: "kgEntrada", label: "📥 Entrada (kg)", color: "#22c55e", disabled: form.tipoMovimiento === "SALIDA" || form.tipoMovimiento === "SCRAP" }, { key: "kgSalida", label: "📤 Salida (kg)", color: "#3b82f6", disabled: form.tipoMovimiento === "ENTRADA" || form.tipoMovimiento === "SCRAP" }, { key: "kgScrap", label: "🗑️ Scrap (kg)", color: "#e63946", disabled: form.tipoMovimiento === "ENTRADA" || form.tipoMovimiento === "SALIDA" }].map(({ key, label, color, disabled }) => (
              <div key={key}>
                <span style={{ ...lbl, marginTop: 0, color: disabled ? "#ccc" : color }}>{label}</span>
                <input type="number" min="0" step="0.01" disabled={disabled} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...inp, textAlign: "center", fontWeight: "700", fontSize: "15px", opacity: disabled ? 0.4 : 1, background: disabled ? (oscuro ? "#091840" : "#f0f0f0") : cardBg }} placeholder="0.00" />
              </div>
            ))}
          </div>
        </div>

        {/* TRAZABILIDAD OPCIONAL */}
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#7B5BF2" }}>Trazabilidad con otros módulos (opcional)</h3>
          <span style={lbl}>ID NC vinculada</span>
          <input onChange={e => setForm(f => ({ ...f, idNC: e.target.value }))} style={inp} placeholder="Ej: NCI-2026-001" />
          <span style={lbl}>ID Plan de Control</span>
          <input onChange={e => setForm(f => ({ ...f, idPlanControl: e.target.value }))} style={inp} placeholder="Ej: PC-IMPRES-001" />
          <span style={lbl}>ID FMEA</span>
          <input onChange={e => setForm(f => ({ ...f, idFMEA: e.target.value }))} style={inp} placeholder="Ej: FMEA-IMPRES-001" />
          <span style={lbl}>Observaciones</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} style={{ ...inp, resize: "none" }} />
        </div>

        <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#22c55e", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginTop: "1rem" }}>
          Registrar Movimiento FSC
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="fsc" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="fsc" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>FSC Arqueo</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => exportarCSV(movsFiltrados, "FSC_Arqueo")}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📊 Excel</button>
          <button onClick={() => exportarPDF(movsFiltrados, { totalEntradas, totalSalidas, totalScrap, stockActual })}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📄 PDF</button>
          {esAdmin && <button onClick={() => setVista("nueva")}
            style={{ padding: "6px 14px", background: "#22c55e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Movimiento</button>}
        </div>
      </div>

      <div style={{ padding: "1rem 1.5rem" }}>
        {/* BALANCE DE MASA */}
        <div style={{ ...card, background: stockActual < 0 ? "#fff0f0" : oscuro ? "#0a1f5c" : "#f0fff4", border: `1px solid ${stockActual < 0 ? "#e63946" : "#22c55e"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: stockActual < 0 ? "#e63946" : "#22c55e", marginBottom: "12px" }}>
            {stockActual < 0 ? "⚠️ ALERTA: Stock negativo — inconsistencia en el balance de masa" : "🌿 Balance de masa FSC Chain of Custody"}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "10px" }}>
            {[["📥 Entradas", `${totalEntradas} kg`, "#22c55e"], ["📤 Salidas", `${totalSalidas} kg`, "#3b82f6"], ["🗑️ Scrap", `${totalScrap} kg`, "#e63946"], ["📦 Stock actual", `${stockActual} kg`, stockActual < 0 ? "#e63946" : "#22c55e"]].map(([lab, val, col]) => (
              <div key={lab} style={{ background: cardBg, borderRadius: "10px", padding: "10px", textAlign: "center", border: `1px solid ${borde}` }}>
                <p style={{ fontSize: "16px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
                <p style={{ fontSize: "10px", color: subtexto, margin: "3px 0 0" }}>{lab}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FILTROS */}
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "1rem" }}>
          <button onClick={() => setFiltroTipo("todos")} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroTipo === "todos" ? "2px solid #7B5BF2" : `1px solid ${borde}`, background: filtroTipo === "todos" ? "rgba(123,91,242,.1)" : cardBg, color: filtroTipo === "todos" ? "#7B5BF2" : subtexto }}>Todos</button>
          {Object.entries(tiposMovimiento).map(([val, { label, color }]) => (
            <button key={val} onClick={() => setFiltroTipo(val)} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroTipo === val ? `2px solid ${color}` : `1px solid ${borde}`, background: filtroTipo === val ? `${color}22` : cardBg, color: filtroTipo === val ? color : subtexto }}>{label}</button>
          ))}
        </div>

        {movsFiltrados.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>🌿</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay movimientos FSC registrados</p>
              <p style={{ fontSize: "13px", color: subtexto }}>Comenzá registrando la primera entrada de material certificado</p>
            </div>
          : [...movsFiltrados].reverse().map(m => (
              <div key={m.id} onClick={() => { setDetalle(m); setVista("detalle"); }}
                style={{ background: m.tipoMovimiento === "SCRAP" ? (oscuro ? "#1a0505" : "#fff5f5") : cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${m.tipoMovimiento === "SCRAP" ? "#e63946" : borde}`, marginBottom: "10px", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,197,94,0.15)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <span style={{ fontWeight: "700", color: "#22c55e", fontSize: "13px" }}>{m.idMovimiento}</span>
                    <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: "700", color: tiposMovimiento[m.tipoMovimiento]?.color, background: `${tiposMovimiento[m.tipoMovimiento]?.color}22`, padding: "2px 6px", borderRadius: "6px" }}>{tiposMovimiento[m.tipoMovimiento]?.label}</span>
                    <p style={{ fontSize: "13px", color: texto, fontWeight: "600", marginTop: "4px" }}>{m.producto}</p>
                    <p style={{ fontSize: "12px", color: subtexto, marginTop: "2px" }}>{m.certificadoFSC} · {m.origen} · {m.fecha}</p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                    {m.kgEntrada > 0 && <span style={{ fontSize: "13px", fontWeight: "700", color: "#22c55e" }}>+{m.kgEntrada} kg</span>}
                    {m.kgSalida > 0 && <span style={{ fontSize: "13px", fontWeight: "700", color: "#3b82f6" }}>-{m.kgSalida} kg</span>}
                    {m.kgScrap > 0 && <span style={{ fontSize: "13px", fontWeight: "700", color: "#e63946" }}>⚠️ {m.kgScrap} kg</span>}
                    <span style={{ fontSize: "10px", color: subtexto }}>Stock: {m.stockAcumulado ?? "—"} kg</span>
                  </div>
                </div>
                <div style={{ marginTop: "6px", fontSize: "11px", color: oscuro ? "#22c55e" : "#4ade80" }}>
                  {m.creadoPor}{m.idNC ? ` · NC: ${m.idNC}` : ""}{m.ot ? ` · OT: ${m.ot}` : ""}
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}